import React, { useCallback, useEffect, useState } from 'react';
import { Eye, EyeOff, ArrowUp, ArrowDown, Save, Info, Plus, Pencil, Trash2 } from 'lucide-react';
import { catalogSelectionApi, customApi } from '../api';
import { useAuth } from '../AuthContext';
import { ENTITY_SCHEMAS } from '../schemas';
import { I18nInput, TextInput } from '../components/Field';
import { ImagePicker } from '../components/ImagePicker';
import { Modal } from '../components/Modal';

const SOURCE_LABEL = { manual: 'Domira', osm: 'OpenStreetMap', provider: 'Automatico', custom: 'Tuo contenuto' };

function CustomContent({ entityKey, scope, onChanged }) {
  const schema = ENTITY_SCHEMAS[entityKey] || { label: entityKey, fields: [] };
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => { setItems(await customApi.list(entityKey, scope)); }, [entityKey, scope]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { data: editing.data, category: editing.category || null };
      if (editing.id) await customApi.update(entityKey, editing.id, body, scope);
      else await customApi.create(entityKey, body, scope);
      setEditing(null);
      await load();
      onChanged?.();
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };
  const remove = async (it) => {
    if (!window.confirm('Eliminare questo tuo contenuto?')) return;
    await customApi.remove(entityKey, it.id, scope);
    await load();
    onChanged?.();
  };
  const disp = (v) => (v && typeof v === 'object' ? v.it || Object.values(v)[0] || '' : v || '');

  return (
    <div className="mt-8" data-testid="custom-content-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg text-paelbi-ink">I tuoi contenuti personalizzati</h2>
          <p className="text-sm text-paelbi-ink/60">Aggiunte tue, visibili solo ai tuoi ospiti. Separate dal catalogo Domira.</p>
        </div>
        <button type="button" data-testid="custom-new-button"
          onClick={() => setEditing({ data: {}, category: '' })}
          className="inline-flex items-center gap-2 rounded-btn border border-paelbi-brown px-4 py-2 text-sm font-semibold text-paelbi-brown hover:bg-paelbi-bg2">
          <Plus size={16} /> Aggiungi
        </button>
      </div>

      <div className="mt-3 overflow-hidden rounded-2xl border border-paelbi-line bg-white" data-testid="custom-items-list">
        {items.length === 0 && <p className="p-4 text-sm text-paelbi-ink/50">Nessun contenuto personalizzato.</p>}
        {items.map((it, i) => (
          <div key={it.id} data-testid={`custom-row-${it.id}`} className={`flex items-center gap-3 p-4 ${i > 0 ? 'border-t border-paelbi-line' : ''}`}>
            {it.data?.image
              ? <img src={it.data.image} alt="" className="h-11 w-11 shrink-0 rounded-lg object-cover" />
              : <div className="h-11 w-11 shrink-0 rounded-lg bg-paelbi-bg2" />}
            <p className="min-w-0 flex-1 truncate font-medium text-paelbi-ink">{disp(it.data?.[schema.titleField]) || '(senza titolo)'}</p>
            <button type="button" data-testid={`custom-edit-${it.id}`} onClick={() => setEditing({ ...it, data: { ...it.data } })} className="rounded-lg p-2 text-paelbi-ink/60 hover:bg-paelbi-bg"><Pencil size={16} /></button>
            <button type="button" data-testid={`custom-delete-${it.id}`} onClick={() => remove(it)} className="rounded-lg p-2 text-red-500 hover:bg-red-50"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? 'Modifica contenuto' : 'Nuovo contenuto personalizzato'} wide
        footer={<>
          <button type="button" onClick={() => setEditing(null)} className="rounded-btn border border-paelbi-line px-4 py-2 text-sm">Annulla</button>
          <button type="button" data-testid="custom-save-button" onClick={save} disabled={saving} className="rounded-btn bg-paelbi-brown px-5 py-2 text-sm font-semibold text-white disabled:opacity-60">{saving ? 'Salvo...' : 'Salva'}</button>
        </>}>
        {editing && (
          <div className="space-y-4">
            {(schema.fields || []).map((f) => {
              const val = editing.data[f.key];
              const set = (v) => setEditing({ ...editing, data: { ...editing.data, [f.key]: v } });
              if (f.type === 'i18n') return <I18nInput key={f.key} label={f.label} value={val} multiline={f.multiline} onChange={set} />;
              if (f.type === 'image') return <ImagePicker key={f.key} label={f.label} value={val} onChange={set} />;
              return <TextInput key={f.key} label={f.label} value={val} onChange={set} />;
            })}
          </div>
        )}
      </Modal>
    </div>
  );
}

export function CatalogSelection({ entityKey }) {
  const { isSuper, tenantId } = useAuth();
  const scope = isSuper ? tenantId : undefined;
  const schema = ENTITY_SCHEMAS[entityKey] || { label: entityKey };
  const [items, setItems] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const disabled = isSuper && !tenantId;

  const load = useCallback(async () => {
    if (disabled) return;
    setItems(await catalogSelectionApi.list(entityKey, scope));
    setDirty(false);
  }, [entityKey, scope, disabled]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, hidden: !it.hidden } : it)));
    setDirty(true);
  };
  const move = (idx, dir) => {
    setItems((prev) => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const hidden = items.filter((i) => i.hidden).map((i) => i.id);
      const order = items.map((i) => i.id);
      await catalogSelectionApi.save(entityKey, { hidden, order }, scope);
      setDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const visibleCount = items.filter((i) => !i.hidden).length;

  return (
    <div data-testid="catalog-selection-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl text-paelbi-ink sm:text-3xl">{schema.label}</h1>
          <p className="text-paelbi-ink/60">Scegli cosa mostrare ai tuoi ospiti e in che ordine. I contenuti sono forniti da Domira.</p>
        </div>
        <button type="button" data-testid="selection-save-button" onClick={save} disabled={saving || !dirty}
          className="inline-flex items-center gap-2 rounded-btn bg-paelbi-brown px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
          <Save size={16} /> {saving ? 'Salvo...' : saved ? 'Salvato ✓' : 'Salva selezione'}
        </button>
      </div>

      <div className="mt-4 flex items-center gap-2 rounded-xl border border-paelbi-line bg-paelbi-bg2/40 px-4 py-3 text-sm text-paelbi-ink/70">
        <Info size={16} className="shrink-0 text-paelbi-brown" />
        {disabled ? 'Seleziona una struttura per gestire i contenuti.' : `${visibleCount} di ${items.length} elementi visibili. Nascondi ciò che non è rilevante per i tuoi ospiti.`}
      </div>

      <div className="mt-4 overflow-hidden rounded-2xl border border-paelbi-line bg-white" data-testid="selection-items-list">
        {items.length === 0 && <p className="p-6 text-sm text-paelbi-ink/50">Nessun contenuto disponibile per la tua zona al momento.</p>}
        {items.map((it, i) => (
          <div key={it.id} data-testid={`selection-row-${it.id}`}
            className={`flex items-center gap-3 p-4 ${i > 0 ? 'border-t border-paelbi-line' : ''} ${it.hidden ? 'opacity-50' : ''}`}>
            {it.image
              ? <img src={it.image} alt="" className="h-12 w-12 shrink-0 rounded-lg object-cover" />
              : <div className="h-12 w-12 shrink-0 rounded-lg bg-paelbi-bg2" />}
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-paelbi-ink">{it.title || '(senza titolo)'}</p>
              <p className="truncate text-xs text-paelbi-ink/50">
                {it.category || '—'}{it.distance ? ` · ${it.distance}` : ''} · <span className="italic">{SOURCE_LABEL[it.source] || it.source}</span>
              </p>
            </div>
            <div className="flex items-center gap-1">
              <button type="button" aria-label="Su" onClick={() => move(i, -1)} disabled={i === 0}
                className="rounded-lg p-2 text-paelbi-ink/50 hover:bg-paelbi-bg disabled:opacity-30"><ArrowUp size={16} /></button>
              <button type="button" aria-label="Giù" onClick={() => move(i, 1)} disabled={i === items.length - 1}
                className="rounded-lg p-2 text-paelbi-ink/50 hover:bg-paelbi-bg disabled:opacity-30"><ArrowDown size={16} /></button>
              <button type="button" data-testid={`selection-toggle-${it.id}`} onClick={() => toggle(it.id)}
                className={`rounded-lg p-2 ${it.hidden ? 'text-paelbi-ink/40 hover:bg-paelbi-bg' : 'text-paelbi-brown hover:bg-paelbi-bg2'}`}
                title={it.hidden ? 'Mostra' : 'Nascondi'}>
                {it.hidden ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
        ))}
      </div>

      {!disabled && <CustomContent entityKey={entityKey} scope={scope} onChanged={load} />}
    </div>
  );
}

export default CatalogSelection;
